import { memo, useEffect, useState, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Popover, Transition } from '@headlessui/react';
import RecordingRow from './RecordingRow';
import DuplicateRecordingModal from './DuplicateRecordingModal';
import Icon from '@/components/atoms/Icon';
import { core } from '@/stores/core';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useRecordingDuplicatesStore } from '@/stores/duplicateRecordingStore';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import useCreateClip from '@/Pages/Clip/ClipPlayer/hooks/useCreateClip';
import { showErrorToast } from '@/libs/toast/toast';

interface RecordingItem {
  id: string;
  title: string;
  label?: string;
  isOriginal?: boolean;
}

function RecordingDuplicateDropdown() {
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const { clipData } = useClipsContext();
  const recordings = useRecordingDuplicatesStore(state => state.recordings);
  const isLoading = useRecordingDuplicatesStore(state => state.isLoading);
  const loadRecordings = useRecordingDuplicatesStore(state => state.loadRecordings);
  const [, setSearchParams] = useSearchParams();

  const { onCreateDuplicate } = useCreateClip({ clip: clipData });

  const [isCreating, setIsCreating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const recordingTitle = coreStore.content?.title || 'Original';
  const activeClipId = clipData.id;
  const originalClipId = coreStore.content?.full_length_asset_id ?? activeClipId;
  const broadcastId = coreStore.content?.id ?? '';

  const existingDuplicatesCount = recordings[broadcastId]?.length ?? 0;
  const defaultDuplicateName = `Duplicate ${existingDuplicatesCount + 1}`;

  const handleConfirmDuplicate = async (name: string) => {
    if (isCreating) {
      return;
    }
    setIsCreating(true);
    try {
      await onCreateDuplicate(name);
      loadRecordings(broadcastId);
      setIsModalOpen(false);
    } catch {
      showErrorToast('Failed to create duplicate. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    if (!featureFlags[FeatureFlagKeys.Use_CL_Clip_Duplication] || !broadcastId) {
      return;
    }
    loadRecordings(broadcastId);
  }, [broadcastId, featureFlags, loadRecordings]);

  const recordingList: RecordingItem[] = [
    { id: originalClipId, title: recordingTitle, label: 'Original', isOriginal: true },
    ...(recordings[broadcastId] ?? [])
  ];

  const isActive = (id: string) => id === activeClipId;

  const handleSelectRecording = (id: string) => {
    const isOriginal = recordingList.find(recording => recording.id === id && recording.isOriginal);
    setSearchParams(
      prev => {
        if (isOriginal) {
          prev.delete('activeDuplicateId');
        } else {
          prev.set('activeDuplicateId', id);
        }
        return prev;
      },
      { replace: true }
    );
  };

  return (
    <Popover className="relative z-50">
      {({ open }) => (
        <>
          <Popover.Button
            className="flex items-center rounded p-1 text-slate-500 hover:bg-slate-200 focus:outline-none"
            aria-label="Duplicate recordings"
          >
            <Icon name={open ? 'IconChevronUp' : 'IconChevronDown'} size={14} />
          </Popover.Button>

          <Transition
            enter="transition duration-150 ease-out"
            enterFrom="transform scale-[0.98] opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-100 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-[0.98] opacity-0"
          >
            <Popover.Panel className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
              {/* Recordings list */}
              <div>
                {isLoading ? (
                  <>
                    <RecordingRow
                      id={originalClipId}
                      title={recordingTitle}
                      label="Original"
                      isOriginal
                      showActionButton={false}
                      isActive={isActive(originalClipId)}
                      onSelect={() => handleSelectRecording(originalClipId)}
                    />
                    <div className="p-2 text-sm text-slate-400">Loading...</div>
                  </>
                ) : (
                  recordingList.map(recording => (
                    <RecordingRow
                      key={recording.id}
                      id={recording.id}
                      title={recording.title}
                      label={recording.label}
                      isOriginal={recording.isOriginal}
                      showActionButton={!recording.isOriginal}
                      isActive={isActive(recording.id)}
                      onSelect={() => handleSelectRecording(recording.id)}
                    />
                  ))
                )}
              </div>

              <hr className="my-1 border-slate-100" />

              <button
                disabled={isCreating}
                onClick={() => setIsModalOpen(true)}
                className="flex w-full items-center gap-2 rounded p-2 text-sm text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400"
              >
                <Icon name="IconPlus" size={14} />
                <span>{isCreating ? 'Creating...' : 'Create duplicate'}</span>
              </button>
            </Popover.Panel>
          </Transition>

          {isModalOpen && (
            <DuplicateRecordingModal
              isOpen={isModalOpen}
              defaultName={defaultDuplicateName}
              isCreating={isCreating}
              onClose={() => setIsModalOpen(false)}
              onConfirm={handleConfirmDuplicate}
            />
          )}
        </>
      )}
    </Popover>
  );
}

export default memo(RecordingDuplicateDropdown);
